import React, { useEffect } from 'react';
import { ExclusionProps, WithMultiSelectToggle } from '@components/design/WithMultiSelectToggle';
import { NO_OPTIONS } from '@utils/StringConstants';
import { FilterKeys, FilterObject, Filters, Option } from '@utils/pageConfig/Types';
import { isAllowSearchBox, isMaxNumOfModelNotNull } from '@utils/FilterUtils';
import { WithSelect } from '@components/design';
import { noop } from 'lodash';

interface FilterItemProductProps {
  isMultiSelector: boolean;
  selectFilter: Filters;
  selectedValue: Option[];
  filterObject: FilterObject;
  onChangeFunc?: (toUpdate: Filters) => void;
  exclusion: ExclusionProps;
}

const FilterSelectorProduct: React.FC<FilterItemProductProps> = ({
  isMultiSelector,
  selectFilter,
  selectedValue,
  filterObject,
  onChangeFunc = noop,
  exclusion,
}) => {
  const [isExclusionActive, setIsExclusionActive] = React.useState<boolean>(exclusion?.isActive === true);

  // Moved this logic to useEffect to avoid updating state during render.
  // React warns against state updates inside render functions (like selectedFilterValue),
  // as it can cause unexpected behavior and infinite re-renders.
  // useEffect ensures the update only happens after render, following React best practices.
  useEffect(() => {
    if (
      selectFilter.filterKey === FilterKeys.PRODUCT_GROUP &&
      selectedValue.length > 0 &&
      !selectFilter.options.find((option) => option.value === selectedValue[0]?.value)
    ) {
      const toUpdate: Filters = {
        dataType: selectFilter.dataType,
        feature: selectFilter.feature,
        filterTitle: selectFilter.filterTitle,
        filterKey: selectFilter.filterKey,
        options: [selectFilter.options[0]],
        disabled: selectFilter.disabled,
        isExclusion: isExclusionActive,
      };
      onChangeFunc?.(toUpdate);
    }
  }, [selectFilter, selectedValue, isExclusionActive, onChangeFunc]);

  useEffect(() => {
    setIsExclusionActive(exclusion?.isActive === true);
  }, [exclusion?.isActive]);

  const shouldShowSelectAll = (selectFilter: Filters) => {
    return [FilterKeys.COUNTRY, FilterKeys.CURRENCY, FilterKeys.PRODUCT_GROUP].includes(selectFilter.filterKey);
  };

  const shouldShowUnselectAll = (selectFilter: Filters) => {
    return [FilterKeys.COUNTRY, FilterKeys.CURRENCY, FilterKeys.PRODUCT_GROUP, FilterKeys.BRAND].includes(
      selectFilter.filterKey,
    );
  };

  const onSelectAll = (selectFilter: Filters, options: Option[]) => {
    const selectedOptions = options;
    if (selectedOptions.length > 0) {
      const toUpdate: Filters = {
        dataType: selectFilter.dataType,
        feature: selectFilter.feature,
        filterTitle: selectFilter.filterTitle,
        filterKey: selectFilter.filterKey,
        options: selectedOptions,
        disabled: selectFilter.disabled,
        isExclusion: isExclusionActive,
      };
      onChangeFunc?.(toUpdate);
    }
  };

  const onUnselectAll = () => {
    const toUpdate: Filters = {
      dataType: selectFilter.dataType,
      feature: selectFilter.feature,
      filterTitle: selectFilter.filterTitle,
      filterKey: selectFilter.filterKey,
      options: [],
      disabled: selectFilter.disabled,
      isExclusion: isExclusionActive,
    };
    onChangeFunc?.(toUpdate);
  };

  const onExclusionChange = (
    checked: boolean,
    event: React.SyntheticEvent<MouseEvent | KeyboardEvent> | MouseEvent,
    id: string,
  ): void => {
    const next = !isExclusionActive;
    setIsExclusionActive(next);

    // When exclusion mode changes, we need to unselect all options
    onUnselectAll();
  };

  if (isMultiSelector) {
    return (
      <div className={'relative'}>
        <WithMultiSelectToggle
          exclusion={exclusion}
          toggleSwitchProps={{ isChecked: isExclusionActive, onChange: onExclusionChange }}
          multiSelectProps={{
            id: 'filter-item',
            name: 'filter-item',
            'data-test-id': `filter-item-${selectFilter.filterKey}`,
            placeholder: 'All',
            options: selectFilter.options,
            disabled:
              selectFilter.options.length === 0 ||
              selectFilter.options[0].label === NO_OPTIONS ||
              isMaxNumOfModelNotNull(filterObject),
            label: selectFilter.filterTitle,
            values: selectedValue.map((v) => v?.value),
            ...(shouldShowSelectAll(selectFilter) && { isPrimaryLinkButtonEnabled: true }),
            ...(shouldShowUnselectAll(selectFilter) && { isSecondaryLinkButtonEnabled: true }),
            ...(shouldShowUnselectAll(selectFilter) && { isVirtualizedList: true }),
            onPrimaryLinkButtonClick: () => onSelectAll(selectFilter, selectFilter.options),
            onSecondaryLinkButtonClick: () => onUnselectAll(),
            onChange: (e: any) => {
              const selectedOptions = selectFilter.options.filter((o) => e.includes(o.value)) ?? [];
              const toUpdate: Filters = {
                dataType: selectFilter.dataType,
                feature: selectFilter.feature,
                filterTitle: selectFilter.filterTitle,
                filterKey: selectFilter.filterKey,
                options: selectedOptions,
                disabled: selectFilter.disabled,
                isExclusion: isExclusionActive,
              };
              onChangeFunc?.(toUpdate);
            },
            combobox: isAllowSearchBox(selectFilter.options),
          }}
        />
      </div>
    );
  }

  return (
    <WithSelect
      id="filter-item"
      name="filter-item"
      key={`filter-item-${selectFilter.filterKey}-${selectFilter.filterTitle}-${selectedValue}`}
      data-test-id={`filter-item-${selectFilter.filterKey}`}
      placeholder={'All'}
      options={selectFilter.options}
      disabled={
        selectFilter.options.length === 0 ||
        selectFilter.options[0].label === NO_OPTIONS ||
        isMaxNumOfModelNotNull(filterObject)
      }
      value={selectedValue[0]?.value}
      label={selectFilter.filterTitle}
      onChange={(e: any) => {
        const selectedOption = selectFilter.options.find((o) => o.value === e);
        if (selectedOption?.value && selectedOption.value !== selectedValue[0].value) {
          const toUpdate: Filters = {
            dataType: selectFilter.dataType,
            feature: selectFilter.feature,
            filterTitle: selectFilter.filterTitle,
            filterKey: selectFilter.filterKey,
            options: [selectedOption],
            disabled: selectFilter.disabled,
          };
          onChangeFunc?.(toUpdate);
        }
      }}
      combobox={isAllowSearchBox(selectFilter.options)}
    />
  );
};

export default FilterSelectorProduct;
